import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { logger } from "../lib/logger";
import { PageHeader, Card, Button, Input, Select, Label, StatusBadge, EmptyState } from "../components/ui-kit";
import { SearchSelect } from "../components/SearchSelect";
import { ColumnFilter, DateRangeFilter, distinctOptions } from "../components/ColumnFilter";
import { Plus, Trash2, FileText, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

const money = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [outstandingByCustomer, setOutstandingByCustomer] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get("status") || "");

  // Sync filter with URL so Dashboard links like /orders?status=pending land correctly.
  useEffect(() => {
    const s = searchParams.get("status") || "";
    if (s !== filter) setFilter(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const changeFilter = (val) => {
    setFilter(val);
    const sp = new URLSearchParams(searchParams);
    if (val) sp.set("status", val); else sp.delete("status");
    setSearchParams(sp, { replace: true });
  };

  const load = async () => {
    const [o, c, p, pl, br] = await Promise.all([
      api.get("/orders"),
      api.get("/customers"),
      api.get("/products"),
      api.get("/price-lists/mine"),
      api.get("/dealer-outstanding").catch(() => ({ data: { rows: [] } })),
    ]);
    setOrders(o.data);
    setCustomers(c.data);
    setProducts(p.data);
    setPriceLists(pl.data);
    // Index outstanding by customer_id so the orders table can render the balance column.
    const map = {};
    for (const row of (br.data?.rows || [])) map[row.customer_id] = row.amount || 0;
    setOutstandingByCustomer(map);
  };

  useEffect(() => {
    load();
    // Initial fetch only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete order?")) return;
    await api.delete(`/orders/${id}`);
    load();
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/orders/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `order_${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      logger.error("order pdf failed:", err);
      toast.error("PDF download failed");
    }
  };

  const [colFilters, setColFilters] = useState({ customer: [], sales: [], terms: [], status: [] });
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const setCol = (k) => (vals) => setColFilters((f) => ({ ...f, [k]: vals }));
  const statusFiltered = filter ? orders.filter((o) => o.status === filter) : orders;
  const filtered = statusFiltered.filter((o) => {
    const d = (o.date || "").slice(0, 10);
    if (dateRange.from && d < dateRange.from) return false;
    if (dateRange.to && d > dateRange.to) return false;
    if (colFilters.customer.length && !colFilters.customer.includes(o.customer_id)) return false;
    if (colFilters.sales.length && !colFilters.sales.includes(o.user_id || "")) return false;
    if (colFilters.terms.length && !colFilters.terms.includes(o.payment_terms || "")) return false;
    if (colFilters.status.length && !colFilters.status.includes(o.status)) return false;
    return true;
  });
  const activeColFilters = Object.values(colFilters).reduce((a, v) => a + (v.length ? 1 : 0), 0) + (dateRange.from || dateRange.to ? 1 : 0);
  const clearColFilters = () => { setColFilters({ customer: [], sales: [], terms: [], status: [] }); setDateRange({ from: "", to: "" }); };
  const canManage = user?.role === "admin" || user?.role === "manager" || user?.role === "director";
  const canChangeStatus = ["admin", "manager", "director", "rm_and_above"].includes(user?.role);
  const canEditOrder = (o) => {
    if (["admin", "manager", "director"].includes(user?.role)) return true;
    return o.user_id === user?.id && o.status === "pending";
  };
  const [editingOrder, setEditingOrder] = useState(null);
  const [overdueOrder, setOverdueOrder] = useState(null);

  return (
    <div>
      <PageHeader
        title="Orders"
        subtitle="Manage customer orders — pending, approved and dispatched"
        actions={
          <>
            <Select value={filter} onChange={(e) => changeFilter(e.target.value)} className="w-40" data-testid="orders-filter">
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="dispatched">Dispatched</option>
              <option value="overdue">Overdue</option>
            </Select>
            <Button onClick={() => setShowForm((s) => !s)} data-testid="new-order-btn">
              <Plus className="w-4 h-4" /> New order
            </Button>
          </>
        }
      />

      {showForm && (
        <OrderForm
          customers={customers}
          products={products}
          priceLists={priceLists}
          onClose={() => setShowForm(false)}
          onCreated={() => {
            setShowForm(false);
            load();
          }}
        />
      )}

      <Card className="p-0 overflow-hidden">
        {activeColFilters > 0 && (
          <div className="flex items-center justify-between px-4 py-2 bg-[#F2EDE4] text-xs text-[#1A2E1C] border-b border-[#E5E2DC]" data-testid="orders-colfilter-bar">
            <span><b>{filtered.length}</b> of {statusFiltered.length} orders · {activeColFilters} column filter{activeColFilters > 1 ? "s" : ""} active</span>
            <button onClick={clearColFilters} className="font-semibold text-[#1E3F20] hover:underline" data-testid="orders-colfilter-clear">Clear all</button>
          </div>
        )}
        {filtered.length === 0 ? (
          <div className="p-8">
            <EmptyState title="No orders yet" description={activeColFilters ? "No orders match the column filters." : "Click 'New order' to record a customer's order."} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
                <tr className="text-left text-[11px] uppercase tracking-widest text-[#5C6B5E]">
                  <th className="px-4 py-3">Order #</th>
                  <th className="px-4 py-3"><DateRangeFilter label="Date" range={dateRange} onChange={setDateRange} testid="orders-colfilter-date" /></th>
                  <th className="px-4 py-3">
                    <ColumnFilter label="Customer" options={distinctOptions(statusFiltered, "customer_id", "customer_name")} selected={colFilters.customer} onChange={setCol("customer")} testid="orders-colfilter-customer" />
                  </th>
                  <th className="px-4 py-3">
                    <ColumnFilter label="Sales person" options={distinctOptions(statusFiltered, "user_id", "user_name", "Unknown")} selected={colFilters.sales} onChange={setCol("sales")} testid="orders-colfilter-sales" />
                  </th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Order Value</th>
                  <th className="px-4 py-3">
                    <ColumnFilter label="Payment terms" options={distinctOptions(statusFiltered, "payment_terms", "payment_terms", "Not set")} selected={colFilters.terms} onChange={setCol("terms")} testid="orders-colfilter-terms" />
                  </th>
                  <th className="px-4 py-3">Outstanding Balance</th>
                  <th className="px-4 py-3">
                    <ColumnFilter label="Status" options={distinctOptions(statusFiltered, "status")} selected={colFilters.status} onChange={setCol("status")} testid="orders-colfilter-status" align="right" />
                  </th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => (
                  <tr key={o.id} className="border-b border-[#E5E2DC] hover:bg-[#F2EDE4]/60" data-testid={`order-row-${o.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs font-bold text-[#1E3F20]" data-testid={`order-number-${o.id}`}>
                      {o.order_number || "—"}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{(o.date || "").slice(0, 10)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{o.customer_name}</div>
                      <div className="text-xs text-[#5C6B5E]">{o.customer_code}</div>
                      {o.ship_to_address && (
                        <div className="text-xs text-[#5C6B5E] mt-1 italic max-w-[260px] truncate" title={o.ship_to_address}>
                          Ship to: {o.ship_to_address}
                        </div>
                      )}
                      {o.price_list_name && (
                        <div className="text-[10px] uppercase tracking-wider text-[#1E3F20] font-bold mt-1">
                          {o.price_list_name}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">{o.user_name}</td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-[#5C6B5E]">
                        {(o.items || []).map((it, i) => (
                          <div key={`${o.id}-${it.product_id}-${i}`}>
                            {it.product_name} × {it.qty} {it.unit || ""} ({it.packing_size})
                          </div>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-semibold" data-testid={`order-value-${o.id}`}>{money(o.total)}</td>
                    <td className="px-4 py-3 text-xs" data-testid={`order-payterms-${o.id}`}>
                      {o.payment_terms || <span className="text-[#8A8577]">—</span>}
                    </td>
                    <td className="px-4 py-3 font-semibold text-red-700" data-testid={`order-outstanding-${o.id}`}>
                      {outstandingByCustomer[o.customer_id] > 0 ? money(outstandingByCustomer[o.customer_id]) : <span className="text-[#8A8577] font-normal">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={o.status} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => downloadPdf(o.id)}
                          className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/10 text-[#1E3F20] hover:bg-[#1E3F20]/20 font-semibold inline-flex items-center gap-1"
                          data-testid={`pdf-${o.id}`}
                          title="Download order PDF"
                        >
                          <FileText className="w-3.5 h-3.5" /> PDF
                        </button>
                        {canEditOrder(o) && (
                          <button
                            onClick={() => setEditingOrder(o)}
                            className="text-xs px-2.5 py-1 rounded bg-[#A3B18A]/20 text-[#1E3F20] hover:bg-[#A3B18A]/40 font-semibold inline-flex items-center gap-1"
                            data-testid={`edit-${o.id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                          </button>
                        )}
                        {canChangeStatus && (
                          <>
                            {o.status !== "approved" && (
                              <button
                                onClick={() => updateStatus(o.id, "approved")}
                                className="text-xs px-2.5 py-1 rounded bg-[#A3B18A]/20 text-[#1E3F20] hover:bg-[#A3B18A]/40 font-semibold"
                                data-testid={`approve-${o.id}`}
                              >
                                Approve
                              </button>
                            )}
                            {o.status !== "dispatched" && (
                              <button
                                onClick={() => updateStatus(o.id, "dispatched")}
                                className="text-xs px-2.5 py-1 rounded bg-[#1E3F20]/15 text-[#1E3F20] hover:bg-[#1E3F20]/25 font-semibold"
                                data-testid={`dispatch-${o.id}`}
                              >
                                Dispatch
                              </button>
                            )}
                            {o.status !== "overdue" && (
                              <button
                                onClick={() => setOverdueOrder(o)}
                                className="text-xs px-2.5 py-1 rounded bg-red-100 text-red-700 hover:bg-red-200 font-semibold"
                                data-testid={`overdue-${o.id}`}
                              >
                                Overdue
                              </button>
                            )}
                            {o.status !== "pending" && (
                              <button
                                onClick={() => updateStatus(o.id, "pending")}
                                className="text-xs px-2.5 py-1 rounded bg-[#DDA15E]/25 text-[#8B5A15] hover:bg-[#DDA15E]/40 font-semibold"
                                data-testid={`revert-${o.id}`}
                              >
                                Mark pending
                              </button>
                            )}
                          </>
                        )}
                        {canManage && (
                          <button onClick={() => del(o.id)} className="text-xs p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`delete-${o.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editingOrder && (
        <OrderForm
          customers={customers}
          products={products}
          priceLists={priceLists}
          initial={editingOrder}
          onClose={() => setEditingOrder(null)}
          onCreated={() => {
            setEditingOrder(null);
            load();
          }}
        />
      )}
      {overdueOrder && (
        <OverdueModal
          order={overdueOrder}
          onClose={() => setOverdueOrder(null)}
          onSaved={async (remark) => {
            try {
              await api.put(`/orders/${overdueOrder.id}/status`, { status: "overdue", remark });
              toast.success("Order marked overdue");
              setOverdueOrder(null);
              load();
            } catch (e) {
              toast.error(formatApiErrorDetail(e?.response?.data?.detail));
            }
          }}
        />
      )}
    </div>
  );
}

function OrderForm({ customers, products, priceLists, initial, onClose, onCreated }) {
  const { user } = useAuth();
  const editing = !!initial;
  const isAdmin = user?.role === "admin";
  const customerLocked = editing && !isAdmin;
  const [customerId, setCustomerId] = useState(initial?.customer_id || "");
  const [orderDate, setOrderDate] = useState((initial?.date || "").slice(0, 10));
  const [priceListId, setPriceListId] = useState(initial?.price_list_id || "");
  const [shipTo, setShipTo] = useState(initial?.ship_to_address || "");
  const [paymentTerms, setPaymentTerms] = useState(initial?.payment_terms || "");
  const [paymentTermsCustom, setPaymentTermsCustom] = useState(
    initial?.payment_terms && !["Cash", "Advance", "7 days", "15 days", "30 days", "60 days"].includes(initial.payment_terms)
      ? initial.payment_terms : ""
  );
  const [items, setItems] = useState(
    initial?.items?.length
      ? initial.items.map((i) => ({ product_id: i.product_id, qty: i.qty, rate: i.rate }))
      : [{ product_id: "", qty: 1, rate: 0 }]
  );
  const [notes, setNotes] = useState(initial?.notes || "");
  const [busy, setBusy] = useState(false);
  const [outstanding, setOutstanding] = useState(null);

  // When customer changes, fetch their outstanding + over-3m so the sales person sees it inline.
  useEffect(() => {
    if (!customerId) { setOutstanding(null); return; }
    let cancelled = false;
    api.get(`/customers/${customerId}/outstanding`).then((res) => {
      if (!cancelled) setOutstanding(res.data);
    }).catch(() => { if (!cancelled) setOutstanding(null); });
    return () => { cancelled = true; };
  }, [customerId]);

  const productById = (id) => products.find((pp) => pp.id === id);
  const activePriceList = priceLists.find((pl) => pl.id === priceListId);

  // When user picks a price list, auto-fill rate for every already-selected product.
  const applyPriceListToAll = (plId) => {
    const pl = priceLists.find((p) => p.id === plId);
    if (!pl) return;
    setItems((prev) =>
      prev.map((it) => {
        if (!it.product_id) return it;
        const match = (pl.items || []).find((x) => x.product_id === it.product_id);
        return match ? { ...it, rate: match.rate } : it;
      })
    );
  };

  const setItem = (i, patch) => {
    setItems((prev) =>
      prev.map((it, idx) => {
        if (idx !== i) return it;
        const next = { ...it, ...patch };
        // If product changed and a price list is active, auto-fill rate from it.
        if (patch.product_id && activePriceList) {
          const match = (activePriceList.items || []).find((x) => x.product_id === patch.product_id);
          if (match) next.rate = match.rate;
        }
        return next;
      })
    );
  };
  const addItem = () => setItems((p) => [...p, { product_id: "", qty: 1, rate: 0 }]);
  const rmItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!customerId) return toast.error("Select a customer");
    if (!items.length || items.some((i) => !i.product_id || !i.qty)) return toast.error("Fill all items");
    setBusy(true);
    try {
      const effectiveTerms = paymentTerms === "Custom" ? paymentTermsCustom.trim() : paymentTerms;
      const payload = {
        customer_id: customerId,
        price_list_id: priceListId || "",
        ship_to_address: shipTo || "",
        payment_terms: effectiveTerms || "",
        items: items.map((i) => {
          const p = productById(i.product_id);
          return {
            product_id: i.product_id,
            product_name: p?.name,
            packing_size: p?.packing_size,
            unit: p?.unit,
            qty: Number(i.qty),
            rate: Number(i.rate) || 0,
          };
        }),
        notes,
      };
      if (editing) {
        const editPayload = {
          items: payload.items,
          notes: payload.notes,
          ship_to_address: payload.ship_to_address,
          price_list_id: payload.price_list_id,
          payment_terms: payload.payment_terms,
        };
        if (isAdmin) {
          editPayload.customer_id = customerId;
          if (orderDate && orderDate !== (initial.date || "").slice(0, 10)) {
            editPayload.date = `${orderDate}${(initial.date || "").slice(10) || "T00:00:00+05:30"}`;
          }
        }
        await api.put(`/orders/${initial.id}`, editPayload);
        toast.success("Order updated");
      } else {
        const r = await api.post("/orders", payload);
        if (r?.data?.queued) toast.info("Offline — order queued. Will sync when back online.");
        else toast.success("Order created");
      }
      onCreated();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const total = items.reduce((a, b) => a + Number(b.qty || 0) * Number(b.rate || 0), 0);
  const formRef = useRef(null);
  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div ref={formRef} className="scroll-mt-4">
    <Card className="mb-6" data-testid="order-form">
      <h3 className="font-heading text-xl font-bold mb-4">
        {editing ? `Edit order ${initial.order_number || ""}` : "New order"}
        {editing && initial.status && (
          <span className="ml-2 align-middle"><StatusBadge status={initial.status} /></span>
        )}
      </h3>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Customer</Label>
            <SearchSelect
              value={customerId}
              onChange={(v) => {
                if (customerLocked) return;
                setCustomerId(v);
                const c = customers.find((x) => x.id === v);
                if (c?.default_price_list_id) {
                  setPriceListId(c.default_price_list_id);
                  applyPriceListToAll(c.default_price_list_id);
                }
                setShipTo("");
              }}
              disabled={customerLocked}
              required
              testId="order-customer"
              placeholder="Search customer by name or code…"
              options={customers.map((c) => ({
                value: c.id,
                label: c.name,
                sublabel: c.code,
                keywords: [c.mobile, c.city].filter(Boolean),
              }))}
            />
            {outstanding && (
              <div
                className="mt-2 flex items-center gap-2 flex-wrap text-xs"
                data-testid="order-outstanding"
              >
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded font-semibold ${outstanding.amount > 0 ? "bg-amber-100 text-amber-900" : "bg-emerald-100 text-emerald-800"}`}>
                  Outstanding: ₹{Number(outstanding.amount || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                </span>
                {Number(outstanding.over_3m_amount) > 0 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-100 text-red-800 font-semibold" data-testid="order-over3m">
                    Over 3M: ₹{Number(outstanding.over_3m_amount).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                  </span>
                )}
                {outstanding.remarks && (
                  <span className="text-[#5C6B5E] italic">"{outstanding.remarks}"</span>
                )}
              </div>
            )}
          </div>
          {editing && isAdmin && (
            <div>
              <Label>Order date</Label>
              <Input
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                data-testid="order-edit-date"
              />
              <div className="text-[11px] text-[#5C6B5E] mt-1">Order number stays {initial.order_number || "unchanged"}.</div>
            </div>
          )}
          <div>
            <Label>Price list</Label>
            <Select
              value={priceListId}
              onChange={(e) => {
                setPriceListId(e.target.value);
                applyPriceListToAll(e.target.value);
              }}
              data-testid="order-price-list"
            >
              <option value="">None — enter rates manually</option>
              {priceLists.map((pl) => (
                <option key={pl.id} value={pl.id}>
                  {pl.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <div>
          <Label>Ship-to address / Delivery point</Label>
          {(() => {
            const cust = customers.find((c) => c.id === customerId);
            const saved = Array.isArray(cust?.ship_to_addresses) ? cust.ship_to_addresses.filter(Boolean) : [];
            return (
              <>
                {saved.length > 0 && (
                  <Select
                    value=""
                    onChange={(e) => {
                      if (e.target.value) setShipTo(e.target.value);
                    }}
                    className="mb-2"
                    data-testid="order-shipto-picker"
                  >
                    <option value="">Pick from saved delivery points…</option>
                    {saved.map((s, i) => (
                      <option key={`saved-${i}`} value={s}>
                        {s}
                      </option>
                    ))}
                  </Select>
                )}
                <textarea
                  value={shipTo}
                  onChange={(e) => setShipTo(e.target.value)}
                  placeholder={
                    saved.length
                      ? "Pick a saved point above or type a one-off delivery address here"
                      : "Optional — leave blank to use customer's default address"
                  }
                  rows={2}
                  data-testid="order-ship-to"
                  className="w-full rounded-lg border border-[#E5E2DC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3F20]/40"
                />
              </>
            );
          })()}
        </div>
        <div className="space-y-3">
          <Label>Items</Label>
          {items.map((it, i) => {
            const prod = productById(it.product_id);
            return (
              <div key={`item-row-${i}`} className="grid grid-cols-12 gap-2 items-start">
                <div className="col-span-12 sm:col-span-5">
                  <SearchSelect
                    value={it.product_id}
                    onChange={(v) => setItem(i, { product_id: v })}
                    testId={`order-product-${i}`}
                    placeholder="Search product…"
                    options={products.map((p) => ({
                      value: p.id,
                      label: p.name,
                      sublabel: p.packing_size,
                      keywords: [p.code, p.category].filter(Boolean),
                    }))}
                  />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Qty"
                      value={it.qty}
                      onChange={(e) => setItem(i, { qty: e.target.value })}
                      data-testid={`order-qty-${i}`}
                    />
                    <span className="text-xs text-[#5C6B5E] whitespace-nowrap min-w-[36px]" data-testid={`order-unit-${i}`}>
                      {prod?.unit || "unit"}
                    </span>
                  </div>
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Rate"
                    value={it.rate}
                    onChange={(e) => setItem(i, { rate: e.target.value })}
                    data-testid={`order-rate-${i}`}
                  />
                </div>
                <div className="col-span-12 sm:col-span-1 flex items-center">
                  <button type="button" onClick={() => rmItem(i)} className="text-red-600 hover:bg-red-50 rounded p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
          <Button variant="outline" type="button" onClick={addItem}>
            <Plus className="w-4 h-4" /> Add item
          </Button>
        </div>
        <div>
          <Label>Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Payment terms</Label>
            <Select
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              data-testid="order-payment-terms"
            >
              <option value="">— Select —</option>
              <option value="Cash">Cash</option>
              <option value="Advance">Advance</option>
              <option value="7 days">7 days</option>
              <option value="15 days">15 days</option>
              <option value="30 days">30 days</option>
              <option value="60 days">60 days</option>
              <option value="Custom">Custom</option>
            </Select>
          </div>
          {paymentTerms === "Custom" && (
            <div>
              <Label>Custom terms</Label>
              <Input
                value={paymentTermsCustom}
                onChange={(e) => setPaymentTermsCustom(e.target.value)}
                placeholder="e.g. 45 days net"
                data-testid="order-payment-terms-custom"
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between pt-2">
          <div className="text-[#5C6B5E]">
            Total: <span className="font-heading text-xl font-bold text-[#1A2E1C]">{money(total)}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy} data-testid="order-submit">
              {busy ? "Saving…" : editing ? "Save changes" : "Create order"}
            </Button>
          </div>
        </div>
      </form>
    </Card>
    </div>
  );
}


function OverdueModal({ order, onClose, onSaved }) {
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!remark.trim()) {
      toast.error("Please add a remark explaining why this order is overdue");
      return;
    }
    setBusy(true);
    try {
      await onSaved(remark.trim());
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg" onClick={(e) => e.stopPropagation()} data-testid="overdue-modal">
        <div className="p-5 border-b border-[#E5E2DC]">
          <div className="font-heading text-xl font-bold text-red-700">Mark order overdue</div>
          <div className="text-xs text-[#5C6B5E] mt-1">Order {order.order_number || order.id.slice(0, 8)} · {order.customer_name}</div>
        </div>
        <div className="p-5 space-y-3">
          <Label>Remark (required)</Label>
          <textarea
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            rows={3}
            placeholder="e.g. Payment delayed by 30 days; dealer requested extension"
            data-testid="overdue-remark"
            className="w-full rounded-lg border border-[#E5E2DC] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
          />
        </div>
        <div className="p-5 border-t border-[#E5E2DC] flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="overdue-submit">
            {busy ? "Saving…" : "Mark overdue"}
          </Button>
        </div>
      </div>
    </div>
  );
}
